import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { Grid2, Typography } from "@mui/material";
import {
    Link, useBlocker, useNavigate, useParams,
} from "react-router-dom";
import Button from "@mui/material/Button";
import { useQuery } from "@tanstack/react-query";
import { useTranslate } from "../../../hooks/useTranslate";
import { useStudents } from "../../../context/students";
import PackageSelection from "./components/PackageSelection";
import "./ReEnrolment.css";
import { useAlumniReEnrolmentStorage } from "./hooks/useAlumniReEnrolmentStorage";
import ReEnrolContracts from "./components/ReEnrolContracts";
import BaseDialog from "../../../components/BaseDialog";
import axiosManager from "../../../services/axiosManager";
import Preloader from "../../../components/Preloader";

const ReEnrolment = () => {
    const { t } = useTranslate();
    const { alumni, refetchAlumni } = useStudents();
    const { studentId } = useParams();
    const navigate = useNavigate();

    const activeStudent = useMemo(() => (alumni || []).find((s) => s.studentId === studentId), [alumni, studentId]);

    const {
        step,
        mode,
        divisionSelected,
        yearGroupSelected,
        startDateSelected,
        packageSelected,
        subjectsSelected,
        summaryText,

        setStep,
        setMode,
        setDivisionSelected,
        setYearGroupSelected,
        setStartDateSelected,
        setPackageSelected,
        setSubjectsSelected,
        setSummaryText,

        hasAlumniReEnrolmentData,
        clearAlumniReEnrolmentStorage,
    } = useAlumniReEnrolmentStorage(studentId);

    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const shouldBlock = useCallback(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return hasAlumniReEnrolmentData;
    }, [hasAlumniReEnrolmentData]);

    const blocker = useBlocker(shouldBlock);

    useEffect(() => () => {
        clearAlumniReEnrolmentStorage();
    }, []);

    const onCancelDialogClose = () => {
        try {
            blocker.reset?.();
            setShowCancelDialog(false);
        } catch {
            // silently fails
        }
    };
    const onCancelDialogContinue = () => {
        try {
            blocker.proceed?.();
            clearAlumniReEnrolmentStorage();
        } catch {
            // silently fails
        }
    };
    const onFinish = () => {
        clearAlumniReEnrolmentStorage();
        refetchAlumni();
        setTimeout(() => {
            navigate(`/dashboard/${studentId}`);
        }, 100);
    };

    return (
        <div className="page-content re-enrolment-page">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height--small mb-8">
                <Grid2 size={{ xs: 12, md: 8, lg: 9 }}>
                    <Link to="/alumni" className="text-white">
                        {t("reEnrol.cancel")}
                    </Link>
                    <Typography variant="h1" className="text-white mt-8">
                        {t("reEnrol.title", { name: activeStudent?.forename })}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("reEnrol.subtitle")}
                    </Typography>
                    {/* <div className="mt-6" />
                    <Link to="/home" className="text-white">
                        {t("reEnrol.needMoreInfo")}
                    </Link> */}
                </Grid2>
            </Grid2>
            <div className="relative z-1">
                <Typography variant="h2" className="text-white">
                    {t("reEnrol.educationalPackage", { name: activeStudent?.forename })}
                </Typography>
                {mode === "selection" ? (
                    <PackageSelection
                        activeStudentId={studentId}
                        activeStudent={activeStudent}
                        studentForename={activeStudent?.forename}
                        onFinish={() => setMode("package")}
                        step={step}
                        divisionSelected={divisionSelected}
                        yearGroupSelected={yearGroupSelected}
                        startDateSelected={startDateSelected}
                        packageSelected={packageSelected}
                        subjectsSelected={subjectsSelected}
                        setStep={setStep}
                        setDivisionSelected={setDivisionSelected}
                        setYearGroupSelected={setYearGroupSelected}
                        setStartDateSelected={setStartDateSelected}
                        setPackageSelected={setPackageSelected}
                        setSubjectsSelected={setSubjectsSelected}
                        setSummaryText={setSummaryText}
                    />
                ) : null}
                {mode === "package" ? (
                    <>
                        <div className="student-box rejoin-package-selection__box--edit mt-2 mb-8">
                            <div className="student-box__main">
                                <div className="student-box__avatar">
                                    {`${activeStudent?.forename?.[0]}${activeStudent?.surname?.[0]}`}
                                </div>
                                <div>
                                    <span className="student-box__title">
                                        {activeStudent?.fullName}
                                    </span>
                                    <Typography variant="body2" component="div">
                                        {summaryText}
                                    </Typography>
                                </div>
                            </div>
                            <Button
                                variant="outlined"
                                onClick={() => setMode("selection")}
                                className="student-box__button--small"
                            >
                                {t("button.edit")}
                            </Button>
                        </div>
                        <ReEnrolContracts
                            activeStudentId={studentId}
                            studentForename={activeStudent?.forename}
                            onFinish={onFinish}
                        />
                    </>
                ) : null}
            </div>
            <BaseDialog
                open={showCancelDialog || blocker.state === "blocked"}
                onClose={onCancelDialogClose}
                title={t("reEnrol.cancelDialogTitle")}
                withCloseButton
            >
                <div className="flex align-center gap-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={onCancelDialogClose}
                    >
                        {t("button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        onClick={onCancelDialogContinue}
                    >
                        {t("button.continue")}
                    </Button>
                </div>
            </BaseDialog>
        </div>
    );
};

export default ReEnrolment;
